/** Which folders an items list starts with, and the folder it shows. */
import { api, type Query } from "../../api/client.ts";
import type { Crumb, FolderList, FolderOut, FolderRow, SpaceOut } from "../../api/types.gen.ts";
import { crumbsOf, type FolderIndex, madeFolders, namedLike, type Place } from "../../lib/folders.ts";
import { spaceApi } from "../../lib/paths.ts";
import { type Resource, useResource } from "../../lib/resource.ts";

/** The folders a list starts with: loaded (or none to load), and each one's breadcrumb when they're from anywhere. */
export interface Listed {
  rows: readonly FolderRow[] | undefined;
  /** A search's folders found by name, from anywhere under the place: where each is. */
  pathOf?: (id: string) => readonly Crumb[];
}

/**
 * Folders in the order the list's items are (`[-]key`): by name for a title, by when made or last changed; by name
 * (as they come) for what folders don't have (a field, a sender).
 */
function sortFolders(rows: readonly FolderRow[], sort: string): readonly FolderRow[] {
  const desc = sort.startsWith("-");
  const key = desc ? sort.slice(1) : sort;
  const by: ((a: FolderRow, b: FolderRow) => number) | undefined =
    key === "title"
      ? (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
      : key === "created" || key === "updated"
        ? (a, b) => {
            const at = key === "created" ? "created_at" : "updated_at";
            return a[at].localeCompare(b[at]);
          }
        : undefined;
  if (!by) {
    return rows;
  }
  return [...rows].sort((a, b) => (desc ? by(b, a) : by(a, b)));
}

/** Whether `filters` (as `GET /items` takes them) narrow a list: then its folders are only those holding matches. */
export function narrowing(filters: Query): boolean {
  // The time zone is no filter.
  return Object.entries(filters).some(
    ([k, v]) => k !== "tz" && v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0),
  );
}

/**
 * How to ask for a list's folders under `filters`: those holding no match too when `unmatched`, or when one made just
 * now may be among them (`keep` then leaves only it of those); the key of what's asked, for caching.
 */
export function folderAsk(filters: Query, unmatched: boolean) {
  const made = madeFolders.value;
  const narrows = narrowing(filters);
  const ask = unmatched || (narrows && made.size > 0);
  return {
    query: ask ? { unmatched: true } : {},
    key: `${unmatched}:${[...made].join(",")}`,
    keep: (rows: readonly FolderRow[]) =>
      unmatched || !narrows ? rows : rows.filter((r) => r.count > 0 || made.has(r.id)),
  };
}

/** The folder a list shows (none at the root). */
export function useFolder(space: SpaceOut, id: string | null): Resource<FolderOut | null> {
  return useResource(`folder:${space.id}:${id ?? ""}`, (signal) =>
    id ? api.get<FolderOut>(spaceApi(space, `/folders/${id}`), undefined, signal) : Promise.resolve(null),
  );
}

/**
 * The folders a list starts with. Listed a level at a time: the place's subfolders holding what the filters match
 * (every one, unfiltered), on its first page only. Flat: a search's folders whose own names match it; otherwise none.
 */
export function useListedFolders(
  space: SpaceOut,
  place: Place,
  filters: Query,
  o: { first: boolean; index: FolderIndex | undefined; sort: string; unmatched: boolean },
): Listed {
  const { first, index } = o;
  const grouped = !place.flat && first;
  const ask = folderAsk(filters, o.unmatched);
  const list = useResource(
    `folder-list:${space.id}:${place.folder ?? ""}:${JSON.stringify(filters)}:${grouped}:${ask.key}`,
    (s) =>
      grouped
        ? api.get<FolderList>(spaceApi(space, "/folders"), { ...filters, parent: place.folder, ...ask.query }, s)
        : Promise.resolve(null),
  );
  if (grouped) {
    // A list the server can't give (an older one, a failure): no folders, the items as they are.
    return { rows: list.error ? [] : list.data ? sortFolders(ask.keep(list.data.data), o.sort) : undefined };
  }
  const q = typeof filters["q"] === "string" ? filters["q"] : "";
  if (!(first && q.trim())) {
    return { rows: [] };
  }
  return { rows: sortFolders(namedLike(index, q, place.folder), o.sort), pathOf: (id) => crumbsOf(index, id) };
}
